import { Capacitor } from '@capacitor/core';
import { useEffect, useRef } from 'react';
import { watchPosBrowserBack } from '../utils/posBrowserBack';

/**
 * P-085 · Codex R2-03: en la web (el Chrome del teléfono), el Atrás con la ayuda abierta CIERRA la ayuda y deja la
 * pantalla donde estaba. En la invitación importa: su enlace vive solo en memoria y salir de la pantalla lo perdía.
 *
 * Es la misma guarda del punto de venta y de «Nuevo producto» (`utils/posBrowserBack.ts`): mientras la ayuda está
 * abierta hay UNA entrada más en el historial, con la misma dirección (sin `hashchange`). El Atrás la saca y cierra el
 * diálogo de más arriba (la pila de `android/androidBackStack.ts`, donde se registra `Modal`); al cerrar con «×» o
 * Escape, la guarda que sobra se quita con un Atrás silencioso. Como en el punto de venta, la guarda se arma al CAMBIAR
 * el estado (nace del toque que abrió la ayuda), nunca al montar: StrictMode monta dos veces. En la app (APK) no se
 * arma: el Atrás físico ya cierra el diálogo.
 *
 * Solo para pantallas sin otra guarda (registro, invitación, inicio de sesión). Dentro de la app, el punto de venta ya
 * tiene la suya; ahí la ayuda se cierra cuando cambia la pantalla (`layouts/AppLayout.tsx`).
 */
export function useInfoDialogBrowserBack(open: boolean): void {
  const openRef = useRef(open);
  openRef.current = open;
  const screen = useRef('');
  const watcher = useRef<{ sync: () => void; stop: () => void }>();
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return undefined;
    const current = watchPosBrowserBack(() => openRef.current, (hash) => hash === screen.current);
    watcher.current = current;
    return () => { current.stop(); watcher.current = undefined; };
  }, []);
  useEffect(() => {
    if (open) screen.current = window.location.hash;
    watcher.current?.sync();
  }, [open]);
}
